import { CalendarMonth } from '@mui/icons-material';
import { BadgeProps, Button, Divider, styled, Typography } from '@mui/material';
import OverdueBanner from './OverdueBanner';
import PeriodCard from 'components/new/PeriodCard';
import StatusBadge from 'components/new/StatusBadge';
import Page from 'components/new/Page';
import { Column } from 'components/Layout';
import { differenceInDays } from 'date-fns';
import { CalendarDay } from 'further-ui/utils';
import { PeriodReviewStatus } from 'further-types/lp/accounting-period';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHistory } from 'react-router-dom';
import { createLpPeriodEndReviewDetailRoute } from 'adminConstants/routes';

const dotStatusMap: Record<PeriodReviewStatus, BadgeProps['color']> = {
  [PeriodReviewStatus.Overdue]: 'error',
  [PeriodReviewStatus.Pending]: 'warning',
  [PeriodReviewStatus.Approved]: 'success',
};

const DateRangeText = styled(Typography)({
  fontWeight: 500,
});

const getPendingStatusLabel = (
  reviewStatus: PeriodReviewStatus,
  dueDate: CalendarDay,
): string => {
  if (reviewStatus === PeriodReviewStatus.Overdue) {
    const daysOverdue = differenceInDays(
      new CalendarDay(new Date()).toUTCDate(),
      dueDate.toUTCDate(),
    );
    return `Overdue by ${daysOverdue} day${daysOverdue === 1 ? '' : 's'}`;
  }
  return `Due by ${dueDate.toReadable()}`;
};

const PeriodEndReview: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const history = useHistory();
  const handleMutation = useHandleTrpcMutation<string>();

  const openPeriodReview = api.accountingPeriod.openPeriodReview.useMutation(
    handleMutation({
      onSuccess: (id) => {
        history.push(createLpPeriodEndReviewDetailRoute(id));
      },
    }),
  );

  const [periods] = api.accountingPeriod.listPeriods.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const pendingPeriods = periods.filter(
    (p) => p.reviewStatus !== PeriodReviewStatus.Approved,
  );
  const overduePeriods = pendingPeriods.filter(
    (p) => p.reviewStatus === PeriodReviewStatus.Overdue,
  );
  const approvedPeriods = periods.filter(
    (p) => p.reviewStatus === PeriodReviewStatus.Approved,
  );

  return (
    <Page
      heading="Period End Review"
      headingIcon={CalendarMonth}
      spacing="lg"
      withHeadingDivider
    >
      <Column spacing="lg">
        {overduePeriods.map((period) => (
          <OverdueBanner
            key={period.periodStart.toString()}
            dueDate={period.dueDate}
          />
        ))}
        <Typography variant="body2" color="text.secondary">
          Periods pending review
        </Typography>
        {pendingPeriods.map((period) => {
          const dateRange = `${period.periodStart.toReadable()} – ${period.periodEnd.toReadable()}`;
          const statusLabel = getPendingStatusLabel(
            period.reviewStatus,
            period.dueDate,
          );
          return (
            <PeriodCard
              key={period.periodStart.toString()}
              icon={<StatusBadge color={dotStatusMap[period.reviewStatus]} />}
              primaryLabel={
                <DateRangeText variant="body2">{dateRange}</DateRangeText>
              }
              secondaryLabel={
                <Typography variant="body2" color="text.secondary">
                  {statusLabel}
                </Typography>
              }
              actions={
                <Button
                  variant="contained"
                  size="small"
                  disableElevation
                  disabled={openPeriodReview.isPending || !vehicle?._id}
                  onClick={() =>
                    openPeriodReview.mutate({
                      vehicleId: vehicle?._id ?? '',
                      periodStart: period.periodStart,
                      periodEnd: period.periodEnd,
                      id: period.existingRecord?.id,
                    })
                  }
                >
                  Review
                </Button>
              }
            />
          );
        })}
        <Divider />
        <Typography variant="body2" color="text.secondary">
          Previously approved periods
        </Typography>
        {approvedPeriods.map((period) => {
          const dateRange = `${period.periodStart.toReadable()} – ${period.periodEnd.toReadable()}`;
          const completedLabel = `Completed ${
            period.approvedAt?.toReadable() ?? 'N/A'
          }${period.approvedByLabel ? ` by ${period.approvedByLabel}` : ''}`;
          return (
            <PeriodCard
              key={period.periodStart.toString()}
              icon={<StatusBadge color="success" />}
              primaryLabel={
                <DateRangeText variant="body2">{dateRange}</DateRangeText>
              }
              secondaryLabel={
                <Typography variant="body2" color="text.secondary">
                  {completedLabel}
                </Typography>
              }
              actions={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    history.push(
                      createLpPeriodEndReviewDetailRoute(
                        period.existingRecord?.id ?? '',
                      ),
                    )
                  }
                >
                  View
                </Button>
              }
            />
          );
        })}
      </Column>
    </Page>
  );
};

export default PeriodEndReview;
